import { CustomerSubEntityTab } from './CustomerSubEntityTab';
import {
  customerBankHooks,
  customerNomineeHooks,
  customerEducationHooks,
  customerOccupationHooks,
  customerIntroducerHooks,
  customerContactHooks,
  customerAddressHooks,
} from '../hooks';

interface TabProps {
  customerId: string;
}

export function CustomerBanksTab({ customerId }: TabProps) {
  return (
    <CustomerSubEntityTab
      title="Bank Accounts"
      addButtonLabel="Add Bank Account"
      emptyLabel="No bank accounts on file"
      customerId={customerId}
      hooks={customerBankHooks}
      fields={[
        { name: 'bankName', label: 'Bank Name', required: true },
        { name: 'branchName', label: 'Branch Name' },
        { name: 'accountNumber', label: 'Account Number', required: true },
        { name: 'ifscCode', label: 'IFSC Code', required: true },
        { name: 'vpaHandle', label: 'UPI VPA (e.g. user@upi)' },
        { name: 'accountType', label: 'Account Type', widget: 'select', options: [{ value: 'SAVINGS', label: 'Savings' }, { value: 'CURRENT', label: 'Current' }, { value: 'OVERDRAFT', label: 'Overdraft' }, { value: 'NRE_NRO', label: 'NRE/NRO' }] },
        { name: 'isPrimary', label: 'Primary Account', widget: 'checkbox' },
      ]}
      columns={[
        { key: 'bankName', label: 'Bank' },
        { key: 'accountNumber', label: 'Account No.' },
        { key: 'ifscCode', label: 'IFSC' },
        { key: 'vpaHandle', label: 'UPI VPA' },
        { key: 'accountType', label: 'Type' },
        { key: 'isPrimary', label: 'Primary', render: (row) => row.isPrimary ? 'Yes' : 'No' },
      ]}
    />
  );
}

export function CustomerNomineesTab({ customerId }: TabProps) {
  return (
    <CustomerSubEntityTab
      title="Nominees"
      addButtonLabel="Add Nominee"
      emptyLabel="No nominees on file"
      customerId={customerId}
      hooks={customerNomineeHooks}
      fields={[
        { name: 'name', label: 'Full Name', required: true },
        { name: 'relation', label: 'Relation' },
        { name: 'dob', label: 'Date of Birth', widget: 'date' },
        { name: 'percentage', label: 'Share %', widget: 'number' },
      ]}
      columns={[
        { key: 'name', label: 'Name' },
        { key: 'relation', label: 'Relation' },
        { key: 'dob', label: 'DOB' },
        { key: 'percentage', label: 'Share %' },
      ]}
    />
  );
}

export function CustomerEducationTab({ customerId }: TabProps) {
  return (
    <CustomerSubEntityTab
      title="Education"
      addButtonLabel="Add Education Record"
      emptyLabel="No education records on file"
      customerId={customerId}
      hooks={customerEducationHooks}
      fields={[
        { name: 'education', label: 'Education', required: true },
        { name: 'spouseEducation', label: "Spouse's Education" },
      ]}
      columns={[
        { key: 'education', label: 'Education' },
        { key: 'spouseEducation', label: "Spouse's Education" },
      ]}
    />
  );
}

export function CustomerOccupationTab({ customerId }: TabProps) {
  return (
    <CustomerSubEntityTab
      title="Occupation"
      addButtonLabel="Add Occupation Record"
      emptyLabel="No occupation records on file"
      customerId={customerId}
      hooks={customerOccupationHooks}
      fields={[
        { name: 'occupationName', label: 'Occupation' },
        { name: 'currentEmployerName', label: 'Employer' },
        { name: 'monthlyIncome', label: 'Monthly Income (in Rupees)', widget: 'number' },
        { name: 'noOfYears', label: 'Years in Occupation', widget: 'number' },
        { name: 'rentedOwnHouse', label: 'Residence', widget: 'select', options: [{ value: 'RENTED', label: 'Rented' }, { value: 'OWN', label: 'Own' }] },
        { name: 'yearsCurrentResidence', label: 'Years at Current Residence', widget: 'number' },
      ]}
      columns={[
        { key: 'occupationName', label: 'Occupation' },
        { key: 'currentEmployerName', label: 'Employer' },
        { key: 'monthlyIncome', label: 'Monthly Income' },
        { key: 'rentedOwnHouse', label: 'Residence' },
      ]}
    />
  );
}

export function CustomerIntroducersTab({ customerId }: TabProps) {
  return (
    <CustomerSubEntityTab
      title="Introducers"
      addButtonLabel="Add Introducer"
      emptyLabel="No introducers on file"
      customerId={customerId}
      hooks={customerIntroducerHooks}
      fields={[
        { name: 'introducerName', label: 'Introducer Name' },
        { name: 'introType', label: 'Introducer Type' },
        { name: 'relationshipWithToMember', label: 'Relationship' },
        { name: 'phoneNo', label: 'Phone' },
        { name: 'email', label: 'Email' },
        { name: 'city', label: 'City' },
      ]}
      columns={[
        { key: 'introducerName', label: 'Name' },
        { key: 'introType', label: 'Type' },
        { key: 'phoneNo', label: 'Phone' },
        { key: 'city', label: 'City' },
      ]}
    />
  );
}

export function CustomerContactsTab({ customerId }: TabProps) {
  return (
    <CustomerSubEntityTab
      title="Phone Numbers & Emails (1-to-Many)"
      addButtonLabel="Add Contact"
      emptyLabel="No additional contacts on file"
      customerId={customerId}
      hooks={customerContactHooks}
      fields={[
        { name: 'type', label: 'Type', widget: 'select', required: true, options: [{ value: 'PHONE', label: 'Phone' }, { value: 'EMAIL', label: 'Email' }] },
        { name: 'label', label: 'Label (e.g. Work, Home, WhatsApp)', required: true },
        { name: 'value', label: 'Value', required: true },
        { name: 'isPrimary', label: 'Primary', widget: 'checkbox' },
      ]}
      columns={[
        { key: 'type', label: 'Type' },
        { key: 'label', label: 'Label' },
        { key: 'value', label: 'Value' },
        { key: 'isPrimary', label: 'Primary', render: (row) => row.isPrimary ? 'Yes' : 'No' },
        { key: 'verified', label: 'Verified', render: (row) => row.verified ? 'Yes' : 'No' },
      ]}
    />
  );
}

export function CustomerAddressesTab({ customerId }: TabProps) {
  return (
    <CustomerSubEntityTab
      title="Addresses (1-to-Many)"
      addButtonLabel="Add Address"
      emptyLabel="No additional addresses on file"
      customerId={customerId}
      hooks={customerAddressHooks}
      fields={[
        {
          name: 'addressType',
          label: 'Address Type',
          widget: 'select',
          options: [
            { value: 'RESIDENTIAL', label: 'Residential' },
            { value: 'OFFICE', label: 'Office' },
            { value: 'PERMANENT', label: 'Permanent' },
            { value: 'COMMUNICATION', label: 'Communication' },
            { value: 'BILLING', label: 'Billing' },
            { value: 'SHIPPING', label: 'Shipping' },
            { value: 'SITE', label: 'Site' },
            { value: 'OTHER', label: 'Other' },
          ],
        },
        { name: 'label', label: 'Label (e.g. Primary, Work HQ)' },
        { name: 'addressLine1', label: 'Address Line 1', required: true },
        { name: 'addressLine2', label: 'Address Line 2' },
        { name: 'city', label: 'City', required: true },
        { name: 'stateCode', label: 'State', required: true },
        { name: 'pincode', label: 'Pincode', required: true },
        { name: 'isPrimary', label: 'Primary', widget: 'checkbox' },
      ]}
      columns={[
        { key: 'addressType', label: 'Type' },
        { key: 'label', label: 'Label' },
        { key: 'addressLine1', label: 'Address' },
        { key: 'city', label: 'City' },
        { key: 'pincode', label: 'Pincode' },
        { key: 'isPrimary', label: 'Primary', render: (row) => row.isPrimary ? 'Yes' : 'No' },
      ]}
    />
  );
}

